/**
 * Airwallex fee estimator — the number the CUSTOMER sees (ADR-0043).
 *
 * Under [[project-zero-cut-principle]] (ADR-0019) Kilat takes no cut of a wash.
 * The operator receives their stated price in full and the customer absorbs the
 * gateway fee as a visible line:
 *
 *     Wash            RM 25.00
 *     Payment fee     RM  0.80
 *     ─────────────────────────
 *     Total           RM 25.80
 *
 * So this module does not compute an internal cost. It computes a number on a
 * car owner's screen, and getting it wrong overcharges or undercharges a real
 * person.
 *
 * ── Why this is not a copy of the toyyibPay estimator ────────────────────────
 *
 * toyyibPay charged **RM 1.00 flat** on FPX, so the fee was independent of the
 * price and the "fee" line barely moved. Airwallex prices local methods as a
 * PERCENTAGE plus a fixed component, like cards. That changes behaviour, not
 * just arithmetic:
 *
 *     RM  25 wash    flat RM 1.00   vs   percentage — comparable
 *     RM 150 detail  flat RM 1.00   vs   percentage — several ringgit
 *
 * A percentage fee on a large detailing package is a number the customer will
 * notice and the operator will be asked about. It is called out in ADR-0043 for
 * that reason; the shape of the fee is a product fact, not an implementation
 * detail.
 *
 * ── THE DEFAULTS BELOW ARE UNCONFIRMED ───────────────────────────────────────
 *
 * ADR-0033's defaulting rule says a value being made configurable keeps exactly
 * the literal that shipped, so an unreachable config document can never quietly
 * change what someone is charged. That rule assumes there IS a shipped literal.
 * For Airwallex there is not: no account exists yet, no rate card has been
 * agreed, and Airwallex prices per merchant.
 *
 * These defaults are therefore a PLACEHOLDER, and the honest thing is to make
 * them announce that rather than look authoritative. `DEFAULT_AIRWALLEX_FEES`
 * carries `confirmed: false`, and `pnpm launch:check` treats unconfirmed PSP
 * pricing as a launch blocker — the platform must not take a real payment while
 * quoting a fee nobody has verified. Set the real rates in
 * `platformConfig/moneyPolicy.psp.airwallex` with `confirmed: true` once the
 * owner's Airwallex pricing is known; nothing else needs redeploying.
 */

/**
 * The numbers that describe Airwallex's pricing to this platform.
 *
 * Structural rather than a Zod type, so this module keeps zero dependencies —
 * `MoneyPolicySchema` builds its `psp.airwallex` defaults from the constant
 * below, which keeps one source of truth for the literals.
 *
 * Percentages are BASIS POINTS (250 = 2.50%) so a stored value is always an
 * integer and no float ever enters a money calculation.
 */
export interface AirwallexFeeTable {
  /** Local-method (FPX / DuitNow QR) percentage in basis points. */
  readonly localPctBasisPoints: number;
  /** Flat sen added on top of the local-method percentage. */
  readonly localFlatSen: number;
  /** Card percentage in basis points — domestic MYR cards. */
  readonly cardPctBasisPoints: number;
  /** Flat sen added on top of the card percentage. */
  readonly cardFlatSen: number;
  /**
   * Ceiling on the TOTAL fee for one payment, in sen.
   *
   * toyyibPay published a real RM 50 card cap. Airwallex does not generally cap
   * percentage pricing, so this exists as a SAFETY RAIL rather than a published
   * rate: it bounds what a misconfigured basis-point value can add to a
   * customer's bill. A cap that never binds costs nothing; the absence of one
   * turns a fat-fingered config edit into an overcharge.
   */
  readonly capSen: number;
  /**
   * Has a human checked these against the real Airwallex rate card?
   *
   * False on the shipped defaults, and deliberately part of the TABLE rather
   * than a separate flag, so it travels with the numbers into
   * `platformConfig/moneyPolicy` and cannot be left behind when they are
   * overridden.
   */
  readonly confirmed: boolean;
}

/**
 * Placeholder rates. NOT a quote from Airwallex — see the header.
 *
 * Chosen to sit in the region of published SEA local-method and domestic-card
 * pricing so the checkout UI renders a plausible line during development. They
 * exist to be replaced, and `confirmed: false` is what stops them being taken
 * for a decision.
 */
export const DEFAULT_AIRWALLEX_FEES: AirwallexFeeTable = {
  localPctBasisPoints: 180,
  localFlatSen: 0,
  cardPctBasisPoints: 290,
  cardFlatSen: 50,
  capSen: 5_000,
  confirmed: false,
};

/**
 * How the customer is paying.
 *
 * `local` covers FPX online banking and DuitNow QR — what Malaysian customers
 * actually use, and what has to be enabled on the Airwallex account (ADR-0043).
 * They are one bucket because they price alike and the customer picks between
 * them on Airwallex's hosted page, after our estimate is already fixed.
 */
export type AirwallexChannel = 'local' | 'card';

export interface AirwallexFee {
  readonly feeSen: number;
  /** i18n KEY, never display text — the receipt renders this. */
  readonly descriptionKey: string;
  /** False when the rate is still the unconfirmed placeholder. */
  readonly confirmed: boolean;
}

/**
 * The customer-visible fee for an amount and channel, in sen.
 *
 * Quotes `local` by default: it is what most Malaysian customers pick, and the
 * conservative choice for the estimate we show before they have chosen. A
 * customer who then pays by card costs marginally more than quoted, and that
 * gap is Kilat's to reconcile — never a surprise added to the customer's total
 * after they have seen it.
 */
export function estimateAirwallexFee(
  amountSen: number,
  channel: AirwallexChannel = 'local',
  fees: AirwallexFeeTable = DEFAULT_AIRWALLEX_FEES,
): AirwallexFee {
  if (amountSen <= 0) {
    return { feeSen: 0, descriptionKey: 'payment.fee.none', confirmed: fees.confirmed };
  }

  const [pct, flat, key] =
    channel === 'local'
      ? ([fees.localPctBasisPoints, fees.localFlatSen, 'payment.fee.local'] as const)
      : ([fees.cardPctBasisPoints, fees.cardFlatSen, 'payment.fee.card'] as const);

  // Integer arithmetic, and `ceil` before the cap: a fee is charged in whole
  // sen, and rounding DOWN would have Kilat absorb a fraction of every single
  // payment — which is a cut, in the direction that quietly costs the platform.
  const raw = Math.ceil((amountSen * pct) / 10_000) + flat;
  return {
    feeSen: Math.min(raw, fees.capSen),
    descriptionKey: key,
    confirmed: fees.confirmed,
  };
}

/**
 * Operator price + fee = what the customer is charged.
 *
 * The three numbers are built together, by one function, so a caller cannot
 * write an intent whose parts do not add up. `amountSen` is what the operator
 * is owed and never varies with the fee — that is the zero-cut principle
 * expressed as an invariant rather than a comment.
 */
export interface AirwallexChargeBreakdown {
  readonly amountSen: number;
  readonly pspFeeSen: number;
  readonly totalSen: number;
  readonly descriptionKey: string;
  readonly confirmed: boolean;
}

export function buildCustomerCharge(
  operatorPriceSen: number,
  channel: AirwallexChannel = 'local',
  fees: AirwallexFeeTable = DEFAULT_AIRWALLEX_FEES,
): AirwallexChargeBreakdown {
  const { feeSen, descriptionKey, confirmed } = estimateAirwallexFee(
    operatorPriceSen,
    channel,
    fees,
  );
  return {
    amountSen: operatorPriceSen,
    pspFeeSen: feeSen,
    totalSen: operatorPriceSen + feeSen,
    descriptionKey,
    confirmed,
  };
}
